import { type ReactElement } from "react";
import type {
  CurrentStateStat,
  CurrentStateStatsParams,
  Tone,
} from "@playbookai/widget-schemas";
import { cn } from "../../lib/utils.js";
import type { WidgetBodyProps } from "../types.js";

/**
 * Current-State Snapshot — Oppress, singleton. 3-col tile grid of baseline
 * metrics with optional delta + tone.
 * Ported from ui-elements/components/solves/workspace/oppress/CurrentStateStatsModule.tsx.
 */
const TONE: Record<Tone, string> = {
  neutral: "text-pb-text-muted",
  good: "text-pb-green-text",
  warn: "text-pb-amber-text",
  bad: "text-pb-red-text",
};

const TILE_TONE: Record<Tone, string> = {
  neutral: "border-pb-border",
  good: "border-pb-green/30",
  warn: "border-pb-amber/40",
  bad: "border-pb-red/30",
};

export function CurrentStateStatsWidget({
  data,
}: WidgetBodyProps<CurrentStateStatsParams>): ReactElement {
  if (data.stats.length === 0) {
    return (
      <div className="text-pb-text-muted text-xs italic py-3 flex-1 grid place-items-center text-center px-4">
        No current-state metrics yet — ask PlayerAI to summarize the gap.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
      {data.stats.map((s) => (
        <Tile key={s.id} stat={s} />
      ))}
    </div>
  );
}

function Tile({ stat }: { stat: CurrentStateStat }): ReactElement {
  const tone = stat.tone ?? "neutral";
  return (
    <div
      className={cn(
        "rounded-md border bg-pb-card-elevated/60 px-3 py-3",
        TILE_TONE[tone],
      )}
    >
      <div className="text-dense-1 uppercase tracking-wider font-semibold text-pb-text-muted">
        {stat.label}
      </div>
      <div className="mt-1.5 text-2xl font-semibold tracking-tight text-pb-text leading-none">
        {stat.value}
      </div>
      {stat.delta ? (
        <div className={cn("mt-1 text-xs font-medium", TONE[tone])}>
          {stat.delta}
        </div>
      ) : null}
    </div>
  );
}
